import { CATEGORY } from '../components/ui/categoryColors';

export type Severity = 'info' | 'advisory' | 'warning' | 'critical';

export type AnalysisCategory =
  | 'motor_esc'
  | 'vibration'
  | 'battery'
  | 'ekf_navigation'
  | 'attitude_control'
  | 'show_performance'
  | 'failsafe_anomaly'
  | 'logged_messages'
  | 'system_power'
  | 'flight_summary';

export interface CategoryResult {
  category: AnalysisCategory;
  severity: Severity;
  metrics: Record<string, any>;
  findings: string[];
  severity_reason: string;
  anomaly_timestamps?: { timestamp_s: number; type: string; value?: string }[];
}

export interface ToolCall {
  tool_name: string;
  params: Record<string, any>;
  response_size?: number;
  duration_ms?: number;
}

export interface AnalysisUsage {
  input_tokens: number;
  output_tokens: number;
  total_cost?: number;
  cache_read_tokens?: number;
}

export interface AnalysisResponse {
  response: string;
  tool_calls: ToolCall[];
  usage: AnalysisUsage;
  iterations: number;
  duration_s?: number;
  log_id?: string;
  drone_serial?: string;
  firmware_branch?: string;
}

export interface ProgrammaticResult {
  summary: string;
  summary_table?: { priority: string; finding: string; action: string }[];
  severity: Severity;
  per_category: Record<string, CategoryResult>;
  drone_serial?: string;
  firmware_branch?: string;
  duration_s?: number;
  log_id?: string;
}

export type CheckStatus = 'PASS' | 'WARN' | 'FAIL' | 'UNKNOWN';

export interface FlightReportCheck {
  name: string;
  status: CheckStatus;
  value: string;
  detail?: string;
  t?: number;
  metrics?: Record<string, number>;
}

export interface FlightReport {
  identity: {
    serial: string;
    branch: string;
    commit: string;
    hardware: string;
    airframe: string;
    duration_s: number;
    boot_utc?: string;
    show_utc_start?: string;
  };
  counts: Record<CheckStatus, number>;
  checks: FlightReportCheck[];
  fleet?: { lines: string[]; note: string; baseline_n: number; siblings_n: number };
  timeline: any[];
  messages: Record<string, any>;
  params: Record<string, any>;
  fields: Record<string, any>;
}

export interface FlightReportResponse {
  log_id: string;
  report: FlightReport;
  text: string;
}

/* ── /analysis/log-charts/:logId: the overview the box draws for every log ── */

export interface LogChart {
  title: string;
  y_label?: string;
  y_start?: number;
  y_range?: [number, number];
  timestamps_s?: number[];
  frequencies?: number[];
  thresholds?: Record<string, number>;
  series: Record<string, (number | null)[]>;
}

export interface LogInfo {
  serial: string;
  duration_s: number;
  firmware: string;
  hardware: string;
  vehicle_uuid: string;
  flight_modes: string[];
}

export interface LogMessage {
  timestamp_s: number;
  level: string;
  message: string;
}

export interface LogChartsResponse {
  log_id: string;
  info: LogInfo;
  logged_messages: LogMessage[];
  charts: Record<string, LogChart>;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  toolCalls?: ToolCall[];
  usage?: AnalysisUsage;
  iterations?: number;
  duration_s?: number;
}

export interface Memory {
  id: number;
  content: string;
  category: string;
  source: 'human' | 'agent';
  added_by: string;
  verified: boolean;
  active: boolean;
  context?: string;
  created_at: string;
  updated_at: string;
}

export interface AnalysisSession {
  id: number;
  title: string;
  drone_serial?: string;
  flight_review_log_id?: string;
  workspace_state: any;
  summary?: string;
  severity?: Severity;
  created_at: string;
  updated_at: string;
}

export interface AgentHealth {
  reachable: boolean;
  status?: string;
  service?: string;
  provider?: string;
  model?: string;
  // null means the box runs one model and there is no Deep/Quick choice to offer
  model_fast?: string | null;
  error?: string;
}

export const SEVERITY_ORDER: Severity[] = ['info', 'advisory', 'warning', 'critical'];

// Chart layer only: the hue a severity paints on a canvas or an SVG, where a class cannot reach.
export const SEVERITY_CONFIG: Record<Severity, { label: string; color: string }> = {
  info:     { label: 'Info',     color: CATEGORY.neutral.ink.hex },
  advisory: { label: 'Advisory', color: CATEGORY.blue.ink.hex },
  warning:  { label: 'Warning',  color: CATEGORY.amber.ink.hex },
  critical: { label: 'Critical', color: CATEGORY.red.ink.hex },
};

// Markup uses these, never the hexes above. Same hue families.
export const SEVERITY_CLASSES: Record<Severity, { text: string; chip: string; ring: string; dot: string }> = {
  info:     { text: 'text-on-surface-variant', chip: 'bg-surface-container-highest text-on-surface-variant', ring: 'ring-outline-variant', dot: 'bg-on-surface-variant' },
  advisory: { text: 'text-blue-400',  chip: 'bg-blue-400/10 text-blue-400',   ring: 'ring-blue-400/40',  dot: 'bg-blue-400' },
  warning:  { text: 'text-amber-400', chip: 'bg-amber-400/10 text-amber-400', ring: 'ring-amber-400/40', dot: 'bg-amber-400' },
  critical: { text: 'text-red-400',   chip: 'bg-red-400/15 text-red-400',     ring: 'ring-red-400/50',   dot: 'bg-red-400' },
};

export const CATEGORY_LABELS: Record<string, { label: string; icon: string }> = {
  motor_esc:         { label: 'Motors/ESC',  icon: '&#9881;' },
  vibration:         { label: 'Vibration',   icon: '&#8776;' },
  battery:           { label: 'Battery',     icon: '&#9889;' },
  ekf_navigation:    { label: 'EKF/Nav',     icon: '&#9678;' },
  attitude_control:  { label: 'Attitude',    icon: '&#8635;' },
  show_performance:  { label: 'Show',        icon: '&#9733;' },
  failsafe_anomaly:  { label: 'Failsafe',   icon: '&#9888;' },
  logged_messages:   { label: 'Messages',    icon: '&#9993;' },
  system_power:      { label: 'Power',       icon: '&#9879;' },
  flight_summary:    { label: 'Summary',     icon: '&#9737;' },
};
